import type {
  ProductCombination,
  ProductCombinationOptionGroup,
} from "~~/shared/types/store";

const normalize = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

/** Grupa atrybutów, którą karta produktu wybiera selektorem rozmiaru. */
export function findSizeGroup(
  options: readonly ProductCombinationOptionGroup[] | undefined | null,
): ProductCombinationOptionGroup | undefined {
  return options?.find((group) => normalize(group.name) === "rozmiar");
}

/**
 * Kod kombinacji to id atrybutów sklejone `_` w kolejności grup z backendu
 * (`3_217` = rozmiar L + kolor Blekitny). Porównujemy ZBIORY id, nie napisy —
 * kolejność grup nie jest częścią kontraktu.
 */
function attributeIdsOf(combination: ProductCombination): Set<number> {
  return new Set(
    combination.combinationCode
      .split("_")
      .map((part) => Number(part.trim()))
      .filter((id) => Number.isInteger(id) && id > 0),
  );
}

export interface SelectedCombination {
  combination: ProductCombination;
  /** Atrybuty TEJ kombinacji w kolejności grup — do etykiety wariantu. */
  attributes: Array<{ group: string; value: string }>;
  /** Czy to kombinacja domyślna produktu (ta, od której pochodzą nazwa i SKU). */
  isDefault: boolean;
}

/**
 * Rozwiązuje wybrany rozmiar na JEDNĄ natywną kombinację PrestaShop.
 *
 * Karta sklepu sprzedającego wybiera wyłącznie rozmiar, a produkt bywa wielowymiarowy
 * (rozmiar × kolor). Pozostałe grupy przyjmują atrybut domyślny backendu —
 * ten sam, od którego pochodzą nazwa i SKU produktu („BOSTON Blekitny XXL").
 * Bez tego stan, zapytanie o wycenę i koszyk szły dla kombinacji domyślnej
 * albo dla sumy wszystkich, niezależnie od kliknięcia.
 *
 * Kolejność wyboru spośród kombinacji z tym rozmiarem: najpierw te NA STANIE
 * (jeśli jakakolwiek jest), potem najbliższa domyślnej (najwięcej zgodnych
 * atrybutów), przy remisie — z największym stanem. Gdy rozmiaru nie ma
 * w domyślnym kolorze (BOSTON: XL tylko w białym) albo domyślny kolor nie ma
 * stanu, etykieta mówi wprost, o jaki kolor chodzi, zamiast pytać
 * o nieistniejącą kombinację, o sumę stanów albo pokazywać „Niedostępny".
 *
 * Sklep katalogowy tu nie trafia: klient wybiera tam kolor sam
 * (app/utils/quoteVariant), a stan hurtowni nie może go zmieniać.
 *
 * `undefined`, gdy produkt nie ma kombinacji, nie ma grupy rozmiaru albo
 * żadna kombinacja nie zawiera wybranego rozmiaru — wołający zostaje wtedy
 * przy danych całego produktu.
 */
export function resolveSelectedCombination(
  options: readonly ProductCombinationOptionGroup[] | undefined | null,
  combinations: readonly ProductCombination[] | undefined | null,
  selectedSize: string,
): SelectedCombination | undefined {
  if (!options?.length || !combinations?.length) return undefined;
  const sizeGroup = findSizeGroup(options);
  const sizeItem = sizeGroup?.items.find(
    (item) => normalize(item.value) === normalize(selectedSize),
  );
  if (!sizeGroup || !sizeItem) return undefined;

  const otherDefaults = options
    .filter((group) => group !== sizeGroup)
    .map((group) => group.defaultId);

  // Kandydat = kombinacja z wybranym rozmiarem i po jednym atrybucie z KAŻDEJ
  // grupy `options`. Przy PS_DISP_UNAVAILABLE_ATTR=0 backend wycina z `options`
  // atrybuty bez stanu, a kombinacja z nimi zostaje w `combinations` — takiej
  // nie da się opisać, więc odpada przed wyborem zwycięzcy.
  const withAttributes = combinations.flatMap((combination) => {
    const ids = attributeIdsOf(combination);
    if (ids.size !== options.length || !ids.has(sizeItem.id)) return [];
    const attributes: SelectedCombination["attributes"] = [];
    for (const group of options) {
      const item = group.items.find((i) => ids.has(i.id));
      if (!item) return [];
      attributes.push({ group: group.name, value: item.value });
    }
    return [{ combination, ids, attributes }];
  });
  if (!withAttributes.length) return undefined;

  // Stan przed zgodnością z domyślnymi: domyślny kolor bez stanu obok innego
  // koloru na stanie dawał „Niedostępny" przy dostępnym rozmiarze.
  const inStock = withAttributes.filter((c) => c.combination.quantity > 0);
  const candidates = (inStock.length ? inStock : withAttributes).map(
    (candidate) => ({
      ...candidate,
      matchedDefaults: otherDefaults.filter(
        (id) => id !== undefined && candidate.ids.has(id),
      ).length,
    }),
  );

  const best = candidates.reduce((winner, candidate) =>
    candidate.matchedDefaults > winner.matchedDefaults ||
    (candidate.matchedDefaults === winner.matchedDefaults &&
      candidate.combination.quantity > winner.combination.quantity)
      ? candidate
      : winner,
  );

  return {
    combination: best.combination,
    attributes: best.attributes,
    isDefault: options.every(
      (group) => group.defaultId !== undefined && best.ids.has(group.defaultId),
    ),
  };
}

/** „Rozmiar: L, Kolor: Blekitny" — czytelna etykieta wybranej kombinacji. */
export function combinationLabel(selected: SelectedCombination): string {
  return selected.attributes.map((a) => `${a.group}: ${a.value}`).join(", ");
}
